import { useEffect, useMemo, useReducer, useRef } from "react";
import { useMatch } from "react-router-dom";

import { wsUrl } from "utils/urls";
import { uuidv4 } from "utils/uuid";

const createRequestState = () => ({
    query: "",
    initialRequirements: null,
});

const createPlanningState = () => ({
    planText: "",
    streaming: false,
    streamBuffer: "",
});

const createResultState = () => ({
    streaming: false,
    isSaving: false,
    report: "",
    sources: [],
    logs: [],
    currentStageMessage: "",
    researchId: null,
    researchNo: null,
    researchUuid: null,
    savedReferenceCount: 0,
    isPersisted: false,
});

const createInitialState = () => ({
    ready: false,
    step: "request",
    phase: "idle",
    error: "",
    requestState: createRequestState(),
    planningState: createPlanningState(),
    resultState: createResultState(),
});

const SOCKET_ACTION_HANDLERS = {
    "session.ready": (state, payload) => ({
        ...state,
        ready: true,
        step: payload.step || "request",
        phase: payload.phase || "idle",
    }),
    "request.accepted": (state, payload) => ({
        ...state,
        step: payload.step || "planning",
        phase: payload.phase || "request_collected",
        error: "",
        planningState: createPlanningState(),
        resultState: createResultState(),
    }),
    "planning.started": (state, payload) => ({
        ...state,
        step: "planning",
        phase: payload.phase || "planning_generation",
        planningState: {
            ...state.planningState,
            streaming: true,
            streamBuffer: "",
        },
    }),
    "planning.delta": (state, payload) => ({
        ...state,
        step: "planning",
        planningState: {
            ...state.planningState,
            streamBuffer: state.planningState.streamBuffer + (payload.content || ""),
        },
    }),
    "planning.completed": (state, payload) => ({
        ...state,
        step: "planning",
        phase: payload.phase || "planning_draft_ready",
        planningState: {
            ...state.planningState,
            streaming: false,
            streamBuffer: "",
            planText: payload.plan_text || "",
        },
    }),
    "planning.confirmed": (state, payload) => ({
        ...state,
        step: payload.step || "result",
        phase: payload.phase || "researching",
        error: "",
    }),
    "research.started": (state, payload) => ({
        ...state,
        step: "result",
        phase: "researching",
        error: "",
        resultState: {
            ...state.resultState,
            streaming: true,
            report: "",
            sources: [],
            logs: [],
            currentStageMessage: "",
            researchId: payload.research_id ?? null,
            researchNo: payload.research_no ?? null,
            researchUuid: payload.research_uuid ?? null,
            savedReferenceCount: 0,
            isPersisted: false,
            isSaving: false,
        },
    }),
    "research.stage": (state, payload) => ({
        ...state,
        resultState: {
            ...state.resultState,
            currentStageMessage: payload.message || "",
        },
    }),
    "research.delta": (state, payload) => ({
        ...state,
        resultState: {
            ...state.resultState,
            report: state.resultState.report + (payload.content || ""),
        },
    }),
    "research.sources": (state, payload) => ({
        ...state,
        resultState: {
            ...state.resultState,
            sources: Array.isArray(payload.sources) ? payload.sources : [],
        },
    }),
    "research.log": (state, payload) => ({
        ...state,
        resultState: {
            ...state.resultState,
            logs: [
                ...state.resultState.logs,
                {
                    stage: payload.stage || "",
                    message: payload.message || "",
                    meta: payload.meta || {},
                },
            ],
        },
    }),
    "research.completed": (state, payload) => ({
        ...state,
        step: "result",
        phase: payload.phase || "completed",
        resultState: {
            ...state.resultState,
            streaming: false,
            isSaving: false,
            currentStageMessage: "",
            report: typeof payload.report === "string" && payload.report ? payload.report : state.resultState.report,
            sources: Array.isArray(payload.sources) ? payload.sources : state.resultState.sources,
            researchId: null,
            researchNo: null,
            researchUuid: null,
            savedReferenceCount: 0,
            isPersisted: false,
        },
    }),
    "research.save.started": (state) => ({
        ...state,
        resultState: {
            ...state.resultState,
            isSaving: true,
        },
    }),
    "research.saved": (state, payload) => ({
        ...state,
        resultState: {
            ...state.resultState,
            isSaving: false,
            researchId: payload.research_id ?? state.resultState.researchId,
            researchNo: payload.research_no ?? state.resultState.researchNo,
            researchUuid: payload.research_uuid ?? state.resultState.researchUuid,
            savedReferenceCount: Number(payload.saved_reference_count || state.resultState.savedReferenceCount || 0),
            isPersisted: !!payload.research_id,
        },
    }),
    "research.stopped": (state, payload) => ({
        ...state,
        phase: payload.phase || "cancelled",
        resultState: {
            ...state.resultState,
            streaming: false,
            isSaving: false,
            currentStageMessage: "",
            researchId: payload.research_id ?? state.resultState.researchId,
        },
    }),
    "step.changed": (state, payload) => {
        const nextStep = payload.step || "request";
        if (nextStep === "planning") {
            return {
                ...state,
                step: nextStep,
                phase: payload.phase || "idle",
                resultState: createResultState(),
            };
        }
        if (nextStep === "request") {
            return {
                ...state,
                step: nextStep,
                phase: payload.phase || "idle",
                requestState: createRequestState(),
                planningState: createPlanningState(),
                resultState: createResultState(),
            };
        }

        return {
            ...state,
            step: nextStep,
            phase: payload.phase || "idle",
        };
    },
    "session.cancelled": (state, payload) => ({
        ...state,
        step: "request",
        phase: payload.phase || "cancelled",
        requestState: createRequestState(),
        planningState: createPlanningState(),
        resultState: createResultState(),
    }),
    error: (state, payload) => ({
        ...state,
        step: payload.step || state.step,
        phase: payload.phase || "error",
        error: payload.message || "오류가 발생했습니다.",
        planningState: {
            ...state.planningState,
            streaming: false,
        },
        resultState: {
            ...state.resultState,
            streaming: false,
            isSaving: false,
            currentStageMessage: "",
        },
    }),
};

function reducer(state, event) {
    switch (event.type) {
        case "SOCKET_MESSAGE": {
            const handler = SOCKET_ACTION_HANDLERS[event.action];
            if (!handler) {
                return state;
            }
            return handler(state, event.payload || {});
        }
        case "SOCKET_CLOSED": {
            return {
                ...state,
                ready: false,
            };
        }
        case "REQUEST_SUBMITTED": {
            return {
                ...state,
                error: "",
                requestState: {
                    query: event.query,
                    initialRequirements: event.initialRequirements,
                },
                planningState: createPlanningState(),
                resultState: createResultState(),
            };
        }
        case "STEP_BACK_REQUESTED": {
            if (event.targetStep === "planning") {
                return {
                    ...state,
                    resultState: createResultState(),
                };
            }
            if (event.targetStep === "request") {
                return {
                    ...state,
                    planningState: createPlanningState(),
                    resultState: createResultState(),
                };
            }
            return state;
        }
        default:
            return state;
    }
}

export function useSearchAgentWebSocket({ connectionDependencies = [] }) {
    const wsRef = useRef(null);
    const connectWebSocketRef = useRef(null);
    const pendingMessagesRef = useRef([]);
    const match = useMatch("/:space_uuid/*");
    const spaceUuid = match?.params?.space_uuid ?? "";

    const [state, dispatch] = useReducer(reducer, undefined, createInitialState);

    const sendMessage = (messageObj) => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
            pendingMessagesRef.current.push(messageObj);
            connectWebSocketRef.current();
            return;
        }

        wsRef.current.send(
            JSON.stringify({
                ...messageObj,
                sender_id: wsRef.current.senderId,
            }),
        );
    };

    const connectWebSocket = () => {
        if (wsRef.current) return;

        wsRef.current = new WebSocket(wsUrl("search"));
        wsRef.current.senderId = uuidv4();

        wsRef.current.onopen = () => {
            sendMessage({ action: "session.init" });
        };

        wsRef.current.onerror = (event) => {
            console.log(event);
        };

        wsRef.current.onclose = () => {
            wsRef.current = null;
            dispatch({ type: "SOCKET_CLOSED" });
        };

        wsRef.current.onmessage = (event) => {
            const data = JSON.parse(event.data);
            const action = data?.action;
            const payload = data?.payload || {};

            dispatch({
                type: "SOCKET_MESSAGE",
                action,
                payload,
            });

            if (action === "session.ready" && pendingMessagesRef.current.length > 0) {
                pendingMessagesRef.current.forEach((message) => {
                    sendMessage(message);
                });
                pendingMessagesRef.current = [];
            }
        };
    };
    connectWebSocketRef.current = connectWebSocket;

    const connectionReady = useMemo(() => connectionDependencies.every(Boolean), [connectionDependencies]);
    useEffect(() => {
        if (connectionReady) {
            connectWebSocketRef.current();
        }
        return () => {
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
        };
    }, [connectionReady]);

    const submitRequest = (query, initialRequirements) => {
        const trimmed = (query || "").trim();
        if (!trimmed) return;
        if (!initialRequirements || typeof initialRequirements !== "object") return;

        dispatch({
            type: "REQUEST_SUBMITTED",
            query: trimmed,
            initialRequirements,
        });

        sendMessage({
            action: "request.submit",
            query: trimmed,
            initial_requirements: initialRequirements,
            space_uuid: spaceUuid,
        });
    };

    const confirmPlanning = (planText) => {
        const trimmed = (planText || "").trim();
        if (!trimmed) return;

        sendMessage({
            action: "planning.confirm",
            confirmed: true,
            plan_text: trimmed,
            space_uuid: spaceUuid,
        });
    };

    const goBackStep = (targetStep) => {
        if (!["request", "planning"].includes(targetStep)) return;

        dispatch({
            type: "STEP_BACK_REQUESTED",
            targetStep,
        });

        sendMessage({
            action: "step.back",
            target_step: targetStep,
        });
    };

    const cancelSession = () => {
        sendMessage({ action: "session.cancel" });
    };

    const saveResearch = () => {
        sendMessage({
            action: "research.save",
            space_uuid: spaceUuid,
        });
    };

    return {
        ready: state.ready,
        step: state.step,
        phase: state.phase,
        error: state.error,
        requestState: state.requestState,
        planningState: state.planningState,
        resultState: state.resultState,
        submitRequest,
        confirmPlanning,
        saveResearch,
        goBackStep,
        cancelSession,
    };
}
